import React from 'react';
import { useNavigate } from 'react-router-dom';
import { EnvelopeIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const SignupMethod: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const signupMethods = [
    {
      id: 'email',
      icon: EnvelopeIcon,
      text: t('signup.continueWithEmail', 'Continue with email'),
      primary: true,
      action: () => navigate('/signup/email'),
    },
    {
      id: 'google',
      icon: () => (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#EA4335"
            d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
          />
        </svg>
      ),
      text: t('signup.continueWithGoogle', 'Continue with Google'),
      action: () => (window.location.href = '/api/auth/google'),
    },
    {
      id: 'apple',
      icon: () => (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.35-1.09-.55-2.08-.56-3.23 0-1.44.71-2.2.51-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.75 1.18-.24 2.31-.93 3.57-.85 1.51.1 2.65.63 3.4 1.62-3.12 1.87-2.38 5.98.48 7.13-.59 1.52-1.36 3.02-2.53 4.32zM11.94 7.22c-.15-2.26 1.66-4.12 3.87-4.3.33 2.58-2.36 4.51-3.87 4.3z" />
        </svg>
      ),
      text: t('signup.continueWithApple', 'Continue with Apple'),
      action: () => (window.location.href = '/api/auth/apple'),
    },
    {
      id: 'phone',
      icon: DevicePhoneMobileIcon,
      text: t('signup.continueWithPhone', 'Continue with phone number'),
      action: () => navigate('/signup/phone'),
    },
  ];

  return (
    <div className="min-h-screen bg-dark-bg dark:bg-dark-bg bg-light-bg flex flex-col">
      <div className="mobile-container flex-1 flex flex-col">
        {/* Header */}
        <header className="flex items-center h-14 mt-safe">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        </header>

        {/* Main content */}
        <div className="flex-1 flex flex-col px-6 pb-8">
          {/* Icon */}
          <div className="flex justify-center mb-8 mt-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">🚀</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-dark-text dark:text-dark-text text-light-text text-center mb-2">
            {t('signup.chooseMethod', 'Choose how to sign up')}
          </h1>

          {/* Subtitle */}
          <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center mb-8">
            {t('signup.pickMethod', 'Pick your preferred sign-up method')}
          </p>

          {/* Sign up methods */}
          <div className="space-y-3">
            {signupMethods.map((method) => {
              const Icon = method.icon;
              return (
                <button
                  key={method.id}
                  onClick={method.action}
                  className={
                    method.primary
                      ? 'btn-primary flex items-center justify-center space-x-3'
                      : 'btn-secondary flex items-center justify-center space-x-3'
                  }
                >
                  {typeof Icon === 'function' &&
                  Icon !== DevicePhoneMobileIcon &&
                  Icon !== EnvelopeIcon ? (
                    <Icon />
                  ) : (
                    <Icon className="w-5 h-5" />
                  )}
                  <span>{method.text}</span>
                </button>
              );
            })}
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-dark-border dark:border-dark-border border-light-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-dark-bg dark:bg-dark-bg bg-light-bg text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                {t('signup.orContinueWith', 'or continue with')}
              </span>
            </div>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => (window.location.href = '/api/auth/google')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 border border-dark-border dark:border-dark-border border-light-border rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5 mx-auto text-dark-text dark:text-dark-text text-light-text"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => (window.location.href = '/api/auth/apple')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 border border-dark-border dark:border-dark-border border-light-border rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5 mx-auto text-dark-text dark:text-dark-text text-light-text"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => navigate('/signup/phone')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-200 border border-dark-border dark:border-dark-border border-light-border rounded-xl transition-colors"
            >
              <svg
                className="w-5 h-5 mx-auto text-dark-text dark:text-dark-text text-light-text"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </button>
          </div>

          {/* Sign in link */}
          <div className="text-center mt-6">
            <span className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
              {t('signup.alreadyHaveAccount', 'Already have an account?')}{' '}
            </span>
            <button
              onClick={() => navigate('/login')}
              className="text-primary-500 hover:text-primary-400 font-medium transition-colors"
            >
              {t('signup.signIn', 'Sign in')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupMethod;
